import { ApprovalState } from '@/services/gql/graphql.generated';
import { dateToApiFormat } from '../../helpers/dateHelper';
import { useWorkingPatternRequests } from './useWorkingPatternRequests';

export const useScheduleRequestStatus = (employeeId?: string) => {
  const { data, fetching, error, refetch } = useWorkingPatternRequests(employeeId);
  const today = dateToApiFormat(new Date());
  const requests = data?.workingPatternRequestsByEmployeeId ?? [];

  const pendingRequest = requests.find(request => request?.state === ApprovalState.Requested);

  const upcomingApprovedRequests = requests
    .filter(
      request =>
        (request?.state === ApprovalState.Approved || request?.state === ApprovalState.Applied) &&
        !!request.effectiveFrom &&
        request.effectiveFrom > today
    )
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));

  // A rejected request never takes effect, so — unlike approved requests — it must stay
  // visible (until dismissed by the caller) regardless of whether its effectiveFrom has
  // since passed; gating on "> today" would silently hide the denial from the employee.
  const upcomingRejectedRequests = requests
    .filter(request => request?.state === ApprovalState.Rejected && !!request.effectiveFrom)
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));

  const upcomingApprovedRequestsByDate = upcomingApprovedRequests.reduce(
    (acc, request) => {
      const date = request.effectiveFrom;
      if (!acc[date]) acc[date] = [];
      acc[date].push(request);
      return acc;
    },
    {} as Record<string, typeof upcomingApprovedRequests>
  );

  return {
    pendingRequest,
    upcomingApprovedRequests,
    upcomingApprovedRequestsByDate,
    upcomingRejectedRequests,
    hasPendingRequest: !!pendingRequest,
    fetching,
    error,
    refetch
  };
};
